import { FGEmpty, FGTextInput, FieldGroup } from "nsitools-react";
import * as React from "react";
import { useHistory, useParams } from "react-router";
import * as Yup from "yup";

import { UserEditProfileDto, UtilisateurApi } from "../../../api";
import { ERoutes } from "../../../AppRouter";
import {
  FGEditableEmailSelectInput,
  FGEditableTelSelectInput,
  FGWalterCheckboxInput,
  FGWalterSelectInput,
  SmallFormGenerator
} from "../../../components";
import { useApiService, useCrudApi, useTl } from "../../../hooks";
import { useReferential } from "../../../hooks/useReferential";
import { ETLCodes } from "../../../locales";
import { phoneRegex } from "../../../utils/phoneRegex";

export interface IProfileDetailPageProps {}

export const ProfileDetailPage: React.FunctionComponent<IProfileDetailPageProps> = props => {
  const { t } = useTl();
  const { id, state } = useParams<{ id: string; tab: string; state: string }>();
  const iduser = React.useMemo(() => +id, [id]);
  const api = useApiService(UtilisateurApi);
  const history = useHistory();

  const { data, loading, saveItem, saving, deleteItem, deleting } = useCrudApi<UserEditProfileDto>(
    React.useMemo(
      () => ({
        getApiFn: () => api.utilisateurGetEditProfile({ idUser: iduser }),
        saveApiFn: (d: UserEditProfileDto) => api.utilisateurSaveEditProfile({ UserEditProfileDto: d }),
        deleteApiFn: d => api.utilisateurDelete({ id: d.iduser }),
        onDeletedRoute: () => ERoutes.profile,
        onSavedRoute: d => `${ERoutes.profile}/${d.iduser}/detail/edit`
      }),
      [api, iduser]
    )
  );

  const idpersonne = React.useMemo(() => data?.idpersonne ?? 0, [data?.idpersonne]);
  const [sexe, sLoading] = useReferential(a => a.referentialGetSexe());

  const FormSchema = React.useMemo(() => {
    return Yup.object().shape({
      nom: Yup.string().required(t(ETLCodes.Required)),
      prenom: Yup.string().required(t(ETLCodes.Required)),
      codesexe: Yup.string().required(t(ETLCodes.Required)),
      telephone: Yup.object().test("tel-valid", t(ETLCodes.ErrorPhoneRegex), function(value) {
        if (this.parent.idapprenant > 0 || !value?.numero) return true;
        const numero = value?.numero as string;
        return !!numero.match(phoneRegex);
      }),
      gsmApprenant: Yup.object().test("gsm-valid", t(ETLCodes.ErrorPhoneRegex), function(value) {
        if (this.parent.idapprenant > 0 || !value?.numero) return true;
        const numero = value?.numero as string;
        return !!numero.match(phoneRegex);
      }),
      email: Yup.object().when("idapprenant", {
        is: idapprenant => idapprenant <= 0,
        then: Yup.object().shape({
          adresseEmail: Yup.string().email(t(ETLCodes.InvalidEmail))
        })
      })
    });
  }, [t]);

  return (
    <SmallFormGenerator
      initialValues={data}
      onSubmit={saveItem}
      editMode={state === "edit"}
      validationSchema={FormSchema}
      loading={loading}
      onCancel={() => history.push(ERoutes.profile)}
      saving={saving}
      onDelete={deleteItem}
      deleting={deleting}
      showDeleteButton={false}
      minLabelWidth={350}
    >
      <FieldGroup fieldsetProps={{ title: t(ETLCodes.Informations) }} columns={2}>
        <FieldGroup>
          <FGTextInput name="nom" label={t(ETLCodes.Nom)} maxLength={20} forceCase="upper" readonly />
          <FGTextInput name="prenom" label={t(ETLCodes.Prenom)} maxLength={50} readonly />
          <FGWalterSelectInput
            name="codesexe"
            label={t(ETLCodes.Genre)}
            items={sexe}
            loading={sLoading}
            isFilterable={false}
            isCleareable={false}
            readonly
          />
          <FGEditableTelSelectInput
            name="gsm.idtelephone"
            label={t(ETLCodes.Gsm)}
            autoSelectIfOne
            idpersonne={idpersonne}
            types={["GSM_PERSO", "GSM_PRO"]}
          />
          <FGEditableTelSelectInput
            name="telephone.idtelephone"
            label={t(ETLCodes.Tel)}
            autoSelectIfOne
            idpersonne={idpersonne}
            types={["PERSO", "PRO"]}
          />
          <FGEditableEmailSelectInput
            name="email.idemail"
            label={t(ETLCodes.General_Email)}
            autoSelectIfOne
            idpersonne={idpersonne}
            readonly
          />
        </FieldGroup>
        <FGEmpty />
      </FieldGroup>

      <FieldGroup fieldsetProps={{ title: t(ETLCodes.Notifications) }} columns={2}>
        <FieldGroup>
          <FGWalterCheckboxInput name="notifySuiviHope" label={t(ETLCodes.NotificationSuiviHopeLabel)} />
          <FGWalterCheckboxInput name="notifySuiviSmile" label={t(ETLCodes.NotificationSuiviSmileLabel)} />
          <FGWalterCheckboxInput name="notifySuiviObserver" label={t(ETLCodes.NotificationSuiviObserverLabel)} />
        </FieldGroup>
        <FGEmpty />
      </FieldGroup>
    </SmallFormGenerator>
  );
};

export default ProfileDetailPage;
